/**
 * Custom-column cell for `formula` columns: read-only output of the column's
 * expression evaluated against the row's other columns.
 *
 * Nothing is stored per row — the result is recomputed on render, so editing a
 * referenced cell updates the formula in the same frame and there is no stale
 * copy to reconcile.
 */
import { Sigma } from 'lucide-react';

import { evaluateFormula, toFormulaNumber } from '../../lib/evaluateFormula';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import { EM_DASH } from './constants';

interface FormulaCustomCellProps {
  /** The column's `config.expression`, e.g. `{Budget} * 0.2`. */
  expression: string | undefined;
  /** Every column on the board — the expression references these by label. */
  columns: CustomColumn[];
  /** The row's stored cell values, keyed by column id. */
  values: Record<string, unknown>;
}

/** Trims the float noise `0.1 + 0.2` leaves behind without truncating integers. */
function formatResult(value: number): string {
  return String(Math.round(value * 1e6) / 1e6);
}

export function FormulaCustomCell({ expression, columns, values }: FormulaCustomCellProps) {
  const variables = new Map<string, number>();
  for (const column of columns) {
    const numeric = toFormulaNumber(values[column.id], column.type);
    if (numeric !== undefined) variables.set(column.label.toLowerCase(), numeric);
  }

  const result = evaluateFormula(expression, variables);

  if (result === null) {
    return (
      <span
        className="flex w-full items-center justify-center text-muted-foreground"
        title={expression}
      >
        {expression ? <Sigma className="size-4" aria-hidden /> : EM_DASH}
      </span>
    );
  }

  return (
    <span className="w-full text-right text-sm tabular-nums" title={expression}>
      {formatResult(result)}
    </span>
  );
}
